import { useState, useEffect } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation, useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '../contexts/SupabaseAuthContext'
import analyticsService from '../services/analyticsService'
import { supabase } from '../config/supabase'
import { ArrowLeft, Check, ChevronDown, ChevronUp } from 'lucide-react'

// --- Componentes Internos ---

const LoginForm = () => {
  const navigate = useNavigate()
  const { signInWithGoogle, signInWithGooglePopup, signInWithEmail, loading, error, user, isAuthenticated } = useAuth()
  const location = useLocation()
  const routeParams = useParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [isGoogleRedirecting, setIsGoogleRedirecting] = useState(false)
  const [expanded, setExpanded] = useState(false)
  
  // Lógica de Referral e Intent (mantida)
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search)
      const ref = params.get('ref') || params.get('code')
      const intent = params.get('intent')
      const returnTo = params.get('return')
      const refFromPath = routeParams?.ref
      
      if (ref) {
        window.localStorage.setItem('overpixel_referral_code', String(ref).toUpperCase())
      } else if (refFromPath) {
        window.localStorage.setItem('overpixel_referral_code', String(refFromPath).toUpperCase())
      }
      
      if (intent) window.localStorage.setItem('postLoginIntent', intent)
      if (returnTo) window.localStorage.setItem('postLoginReturn', returnTo)

      // Se já logado, processar intent
      if (isAuthenticated && user?.id && intent) {
        (async () => {
          try {
            if (intent === 'approve_beta') {
              const { error } = await supabase.from('acesso_beta').upsert({ user_uid: user.id, source: 'invite', status: 'approved' }, { onConflict: 'user_uid' })
              if (!error) try { window.localStorage.setItem('showBetaApprovedModal', 'true') } catch {}
            } else if (intent === 'join_beta') {
              await supabase.from('acesso_beta').upsert({ user_uid: user.id, source: 'post_login', status: 'pending' }, { onConflict: 'user_uid' })
            }
          } catch (e) {
            console.error('Falha ao aplicar intent:', e)
          } finally {
            window.localStorage.removeItem('postLoginIntent')
            window.localStorage.removeItem('postLoginReturn')
            navigate((returnTo || (intent === 'approve_beta' ? '/chat' : '/')))
          }
        })()
      }
    } catch (e) {
      console.warn('Erro ao processar URL auth:', e)
    }
  }, [location.search, routeParams?.ref, isAuthenticated, user?.id])
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await signInWithEmail(email, password)
      const params = new URLSearchParams(location.search)
      const intent = params.get('intent') || window.localStorage.getItem('postLoginIntent') || ''
      const returnTo = params.get('return') || window.localStorage.getItem('postLoginReturn') || '/dashboard'
      
      // Aplicar intents pós-login
      try {
        const { data: authData } = await supabase.auth.getUser()
        const uid = authData?.user?.id
        if (uid && intent) {
          if (intent === 'join_beta') {
            await supabase.from('acesso_beta').upsert({ user_uid: uid, source: 'post_login', status: 'pending' }, { onConflict: 'user_uid' })
          } else if (intent === 'approve_beta') {
            await supabase.from('acesso_beta').upsert({ user_uid: uid, source: 'invite', status: 'approved' }, { onConflict: 'user_uid' })
            try { window.localStorage.setItem('showBetaApprovedModal', 'true') } catch {}
          }
        }
      } catch (err) {
        console.error(err)
      } finally {
        window.localStorage.removeItem('postLoginIntent')
        window.localStorage.removeItem('postLoginReturn')
        navigate(returnTo)
      }
    } catch (error) {
      console.log('Login falhou:', error)
    }
  }

  const handleGoogleLogin = async () => {
    try {
      // Garantir que o parâmetro de retorno seja salvo antes do redirecionamento
      const params = new URLSearchParams(location.search)
      const returnTo = params.get('return')
      if (returnTo) {
        window.localStorage.setItem('postLoginReturn', returnTo)
      }
      
      setIsGoogleRedirecting(true)
      await signInWithGoogle()
    } catch (err) {
      console.error(err)
      setIsGoogleRedirecting(false)
    }
  }
  
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md mx-auto">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">Bem-vindo de volta</h2>
        <p className="text-gray-400">Entre para continuar criando com a Overpixel.</p>
      </div>

      {/* Botão Google Principal */}
      <button 
        onClick={handleGoogleLogin}
        disabled={loading || isGoogleRedirecting}
        className="w-full h-12 bg-white text-black font-semibold rounded-lg hover:bg-gray-100 transition-all flex items-center justify-center gap-3 mb-6 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-white/5"
      >
        {isGoogleRedirecting ? (
          <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
        ) : (
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.84z" />
            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
        )}
        <span>Continuar com Google</span>
      </button>

      <div className="flex items-center gap-4 mb-6">
        <div className="h-px bg-gray-800 flex-1" />
        <span className="text-xs text-gray-500 font-medium">OU ENTRAR COM EMAIL</span>
        <div className="h-px bg-gray-800 flex-1" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#0F0F0F] border border-gray-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                placeholder="seu@email.com"
                required
              />
            </div>
            
            {expanded && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-400 mb-1.5">Senha</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-[#0F0F0F] border border-gray-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                    placeholder="Sua senha"
                    required
                  />
                </div>
                
                <div className="flex items-center justify-between mb-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-700 bg-[#0F0F0F] text-purple-600 focus:ring-purple-500 focus:ring-offset-0"
                    />
                    <span className="text-sm text-gray-400">Lembrar-me</span>
                  </label>
                  <Link to="/auth/reset-password" className="text-sm text-purple-400 hover:text-purple-300">
                    Esqueceu a senha?
                  </Link>
                </div>

                <button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 rounded-lg transition-colors disabled:opacity-50"
                >
                  {loading ? 'Entrando...' : 'Entrar com Email'}
                </button>
              </motion.div>
            )}
        </div>
      </form>

      {!expanded && (
        <button 
          onClick={() => setExpanded(true)}
          className="w-full mt-4 py-3 text-gray-400 hover:text-white text-sm font-medium flex items-center justify-center gap-2 transition-colors border border-dashed border-gray-800 rounded-lg hover:border-gray-600"
        >
          <span>Mostrar opções de email</span>
          <ChevronDown size={16} />
        </button>
      )}

      <p className="mt-8 text-center text-sm text-gray-500">
        Não tem uma conta?{' '}
        <Link to="/auth/register" className="text-white hover:underline font-medium">
          Cadastre-se gratuitamente
        </Link>
      </p>
    </motion.div>
  )
}

const RegisterForm = () => {
  const navigate = useNavigate()
  const { signUpWithEmail, signInWithGoogle, loading, error } = useAuth()
  const location = useLocation()
  const routeParams = useParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [name, setName] = useState('')
  const [isGoogleRedirecting, setIsGoogleRedirecting] = useState(false)
  const [referralInput, setReferralInput] = useState('')
  
  // Lógica de referral (mantida)
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search)
      const ref = params.get('ref') || params.get('code')
      const refFromPath = routeParams?.ref
      
      if (ref) {
        setReferralInput(String(ref).toUpperCase())
        window.localStorage.setItem('overpixel_referral_code', String(ref).toUpperCase())
      } else if (refFromPath) {
        setReferralInput(String(refFromPath).toUpperCase())
        window.localStorage.setItem('overpixel_referral_code', String(refFromPath).toUpperCase())
      }
    } catch (e) {}
  }, [location.search, routeParams?.ref])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (password !== confirmPassword) return alert('As senhas não coincidem')
    try {
      if (referralInput) window.localStorage.setItem('overpixel_referral_code', String(referralInput).toUpperCase())
      await signUpWithEmail(email, password, name)
      navigate('/dashboard')
    } catch (err) { console.error(err) }
  }

  const handleGoogleSignUp = async () => {
    try {
      setIsGoogleRedirecting(true)
      if (referralInput) window.localStorage.setItem('overpixel_referral_code', String(referralInput).toUpperCase())
      await signInWithGoogle() // MUDANÇA: Redirect em vez de Popup
    } catch (err) { console.error(err) } finally { 
      // Não resetar loading se for redirect, pois a página vai mudar
      // setIsGoogleRedirecting(false) 
    }
  }

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md mx-auto">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">Crie sua conta</h2>
        <p className="text-gray-400">Junte-se à comunidade Overpixel hoje.</p>
      </div>

      <button 
        onClick={handleGoogleSignUp}
        disabled={loading || isGoogleRedirecting}
        className="w-full h-12 bg-white text-black font-semibold rounded-lg hover:bg-gray-100 transition-all flex items-center justify-center gap-3 mb-6 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-white/5"
      >
        {isGoogleRedirecting ? <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : (
          <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" /><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /><path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.84z" /><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" /></svg>
        )}
        <span>Cadastrar com Google</span>
      </button>

      <div className="flex items-center gap-4 mb-6">
        <div className="h-px bg-gray-800 flex-1" />
        <span className="text-xs text-gray-500 font-medium">OU PREENCHA OS DADOS</span>
        <div className="h-px bg-gray-800 flex-1" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">{error}</div>}
        
        <div className="grid grid-cols-1 gap-4">
          <input type="text" placeholder="Código de indicação (opcional)" value={referralInput} onChange={e => setReferralInput(e.target.value.toUpperCase())} className="bg-[#0F0F0F] border border-gray-800 rounded-lg px-4 py-3 text-white text-sm focus:border-purple-500 focus:outline-none transition-colors uppercase tracking-wider" />
          <input type="text" placeholder="Nome completo" value={name} onChange={e => setName(e.target.value)} required className="bg-[#0F0F0F] border border-gray-800 rounded-lg px-4 py-3 text-white focus:border-purple-500 focus:outline-none transition-colors" />
          <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required className="bg-[#0F0F0F] border border-gray-800 rounded-lg px-4 py-3 text-white focus:border-purple-500 focus:outline-none transition-colors" />
          <input type="password" placeholder="Senha" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} className="bg-[#0F0F0F] border border-gray-800 rounded-lg px-4 py-3 text-white focus:border-purple-500 focus:outline-none transition-colors" />
          <input type="password" placeholder="Confirmar senha" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required minLength={6} className="bg-[#0F0F0F] border border-gray-800 rounded-lg px-4 py-3 text-white focus:border-purple-500 focus:outline-none transition-colors" />
        </div>

        <button type="submit" disabled={loading} className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 rounded-lg transition-colors disabled:opacity-50 mt-2">
          {loading ? 'Criando conta...' : 'Criar Conta Grátis'}
        </button>
      </form>

      <p className="mt-8 text-center text-sm text-gray-500">
        Já tem uma conta?{' '}
        <Link to="/auth/login" className="text-white hover:underline font-medium">Entrar</Link>
      </p>
    </motion.div>
  )
}

const ResetPasswordForm = () => {
  const { resetPassword, loading, error } = useAuth()
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await resetPassword(email)
      setSubmitted(true)
    } catch (e) {}
  }

  if (submitted) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center w-full max-w-md mx-auto">
        <div className="w-16 h-16 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check size={32} />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Email Enviado!</h2>
        <p className="text-gray-400 mb-8">Verifique sua caixa de entrada e siga as instruções para redefinir sua senha.</p>
        <Link to="/auth/login" className="text-purple-400 hover:text-purple-300 font-medium">Voltar para o Login</Link>
      </motion.div>
    )
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full max-w-md mx-auto">
      <Link to="/auth/login" className="inline-flex items-center text-gray-500 hover:text-white mb-8 transition-colors text-sm">
        <ArrowLeft size={16} className="mr-2" /> Voltar
      </Link>
      <h2 className="text-3xl font-bold text-white mb-2">Recuperar Senha</h2>
      <p className="text-gray-400 mb-8">Digite seu email para receber o link de recuperação.</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">{error}</div>}
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-1.5">Email cadastrado</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full bg-[#0F0F0F] border border-gray-800 rounded-lg px-4 py-3 text-white focus:border-purple-500 focus:outline-none transition-colors" placeholder="seu@email.com" />
        </div>
        <button type="submit" disabled={loading} className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 rounded-lg transition-colors disabled:opacity-50">
          {loading ? 'Enviando...' : 'Enviar Instruções'}
        </button>
      </form>
    </motion.div>
  )
}

// --- Componente Hero (Coluna Direita) ---
const AuthHero = () => {
  return (
    <div className="relative w-full h-full flex items-center justify-center overflow-hidden bg-black border-l border-white/5">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/10 via-black to-cyan-900/10" />
      <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] bg-purple-600/20 rounded-full blur-[120px] animate-pulse" />
      <div className="absolute bottom-1/4 left-1/4 w-[500px] h-[500px] bg-cyan-600/20 rounded-full blur-[120px] animate-pulse delay-1000" />
      
      {/* Noise Texture */}
      <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.65\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E")' }} />
      
      {/* Content */}
      <div className="relative z-10 max-w-lg text-center p-12">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9, y: 20 }} 
          animate={{ opacity: 1, scale: 1, y: 0 }} 
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="mb-12 relative"
        >
          <div className="w-24 h-24 mx-auto bg-gradient-to-br from-gray-900 to-black rounded-3xl flex items-center justify-center shadow-2xl shadow-purple-500/20 border border-white/10 mb-8 transform -rotate-6 hover:rotate-0 transition-transform duration-500">
            <span className="text-4xl filter drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]">✨</span>
          </div>
          
          <h1 className="text-5xl lg:text-6xl font-black text-white mb-6 tracking-tight leading-[1.1]">
            Crie o inimaginável com <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">IA</span>
          </h1>
          
          <p className="text-lg text-gray-400 leading-relaxed max-w-md mx-auto font-medium">
            Acesse ferramentas exclusivas de upscaling, geração de assets e automação de fluxo de trabalho. Sua criatividade não tem mais limites.
          </p>
        </motion.div>
        
        {/* Carousel Dots Mock */}
        <div className="flex justify-center gap-3 mt-12">
          <motion.div initial={{ width: 8 }} animate={{ width: 24 }} className="h-2 rounded-full bg-white" />
          <div className="w-2 h-2 rounded-full bg-white/20" />
          <div className="w-2 h-2 rounded-full bg-white/20" />
        </div>
      </div>
    </div>
  )
}

// --- Componente Principal (Page) ---

const AuthPage = () => {
  const { isAuthenticated } = useAuth()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const hasIntent = !!params.get('intent')

  if (isAuthenticated && !hasIntent) {
    return <><Navigate to="/dashboard" replace /></>
  }

  return (
    <div className="min-h-screen bg-black flex overflow-hidden">
      {/* Coluna Esquerda: Formulários (40-50%) */}
      <div className="w-full lg:w-[45%] xl:w-[40%] flex flex-col relative z-20 bg-black/50 backdrop-blur-md lg:bg-black border-r border-white/5">
        
        {/* Header Mobile/Tablet */}
        <div className="p-6 lg:p-8 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 bg-gradient-to-br from-white to-gray-400 rounded-lg flex items-center justify-center group-hover:shadow-[0_0_15px_rgba(255,255,255,0.3)] transition-all duration-300">
              <div className="w-3 h-3 bg-black rounded-sm transform rotate-45" />
            </div>
            <span className="text-xl font-bold tracking-tight text-white group-hover:text-gray-200 transition-colors">
              OVERPIXEL
            </span>
          </Link>
        </div>

        {/* Container Centralizado Verticalmente */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-12 overflow-y-auto custom-scrollbar">
          <Routes>
            <Route path="/" element={<LoginForm />} />
            <Route path="/login" element={<LoginForm />} />
            <Route path="/login/:ref" element={<LoginForm />} />
            <Route path="/register" element={<RegisterForm />} />
            <Route path="/register/:ref" element={<RegisterForm />} />
            <Route path="/reset-password" element={<ResetPasswordForm />} />
            <Route path="*" element={<Navigate to="/auth/login" replace />} />
          </Routes>
        </div>

        {/* Footer */}
        <div className="p-6 lg:p-8 text-center lg:text-left">
          <p className="text-xs text-gray-600">
            © 2026 Overpixel AI. Todos os direitos reservados.
          </p>
        </div>
      </div>

      {/* Coluna Direita: Hero/Visual (Hidden on Mobile) */}
      <div className="hidden lg:block flex-1 relative bg-black">
        <AuthHero />
      </div>
    </div>
  )
}

export default AuthPage
